import { Link } from "react-router-dom";
import { Linkedin, Instagram, Youtube, Facebook } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const footerLinks = {
  navigate: [
    { href: "/why", label: "Why" },
    { href: "/how", label: "How" },
    { href: "/what", label: "What" },
    { href: "/who", label: "Who" },
  ],
  explore: [
    { href: "/projects", label: "Projects" },
    { href: "/contact", label: "Contact" },
  ],
  services: [
    { href: "/what#branding", label: "Branding" },
    { href: "/what#marketing", label: "Marketing" },
    { href: "/what#creative", label: "Creative" },
  ],
};

const Footer = () => {
  return (
    <footer className="bg-black border-t border-white/10">
      <div className="container-wide section-padding-sm">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Reveal>
              <Link to="/" className="flex items-center gap-3 mb-2">
                <img src="/assets/images/nueva_logo.png" alt="Nueva Media Solutions Logo" className="h-8 object-contain brightness-0 invert" />
                <span className="text-xl font-bold tracking-tight leading-none text-white flex items-center">
                  Nueva Media Solutions
                </span>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                Do What Matters!
              </p>
            </Reveal>
            <div className="flex gap-4">
              {[
                { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/nuevamediasolutions" },
                { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/nuevamediasolutions" },
                { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/nueva-media-solutions" },
                { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@NuevaSolutions" }
              ].map((social, i) => (
                <Reveal key={social.label} delay={0.2 + (i * 0.05)}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#c0ff09] hover:border-[#c0ff09] transition-all duration-300"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Navigate Column */}
          <div>
            <Reveal className="mb-6">
              <h4 className="text-xs font-bold tracking-[0.2em] text-white/40 text-left">
                NAVIGATE
              </h4>
            </Reveal>
            <ul className="space-y-3">
              {footerLinks.navigate.map((link, i) => (
                <li key={link.href}>
                  <Reveal delay={0.1 + (i * 0.05)} className="text-left">
                    <Link
                      to={link.href}
                      className="text-white hover:text-[#c0ff09] transition-colors duration-300 text-sm"
                    >
                      {link.label}
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore Column */}
          <div>
            <Reveal className="mb-6">
              <h4 className="text-xs font-bold tracking-[0.2em] text-white/40 text-left">
                EXPLORE
              </h4>
            </Reveal>
            <ul className="space-y-3">
              {footerLinks.explore.map((link, i) => (
                <li key={link.href}>
                  <Reveal delay={0.1 + (i * 0.05)} className="text-left">
                    <Link
                      to={link.href}
                      className="text-white hover:text-[#c0ff09] transition-colors duration-300 text-sm"
                    >
                      {link.label}
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <Reveal className="mb-6">
              <h4 className="text-xs font-bold tracking-[0.2em] text-white/40 text-left">
                SERVICES
              </h4>
            </Reveal>
            <ul className="space-y-3">
              {footerLinks.services.map((link, i) => (
                <li key={link.href}>
                  <Reveal delay={0.1 + (i * 0.05)} className="text-left">
                    <Link
                      to={link.href}
                      className="text-white hover:text-[#c0ff09] transition-colors duration-300 text-sm"
                    >
                      {link.label}
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Reveal>
              <p className="text-muted-foreground text-sm">
                © {new Date().getFullYear()} Nueva Media Solutions. Do What Matters! All rights reserved.
              </p>
            </Reveal>
            <div className="flex gap-6">
              <Reveal delay={0.1}>
                <Link
                  to="/privacy"
                  className="text-muted-foreground hover:text-foreground text-sm transition-colors duration-300"
                >
                  Privacy Policy
                </Link>
              </Reveal>
              <Reveal delay={0.2}>
                <Link
                  to="/terms"
                  className="text-muted-foreground hover:text-foreground text-sm transition-colors duration-300"
                >
                  Terms of Service
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
